import React, {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {getRunningHourCounters, updatePmsPlanLine} from '../../core/pms/_requests'
import {getEquipmentByVessel, getSubcomponentsByEquipment} from '../../core/_requests'
import type {
  PmsPlanLineDto,
  PmsWorkType,
  PmsScheduleType,
  PmsIntervalUnit,
  PmsCriticality,
  RunningHourCounterDto,
} from '../../core/pms/_models'
import type {EquipmentDto, SubcomponentDto} from '../../core/_models'
import {KTSVG} from '../../../../../_metronic/helpers'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

interface Props {
  visible: boolean
  planLine: PmsPlanLineDto
  vesselId: number
  onClose: () => void
  onSuccess: () => void
  locked?: boolean
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(15, 23, 42, 0.45)',
  position: 'fixed',
  inset: 0,
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(2px)',
  padding: '24px',
  overflow: 'auto',
  overscrollBehavior: 'contain',
}

// Job Types - single letters only (A-Z)
const JOB_TYPE_WORKTYPE_MAP: Record<string, PmsWorkType> = {
  I: 'INSPECT',
  G: 'CHECK',
  O: 'OVERHAUL',
  C: 'CLEAN',
  A: 'ADJUST',
  R: 'RENEW',
  S: 'CHECK',
  T: 'TEST',
  M: 'CHECK',
  D: 'CHECK',
  L: 'CHECK',
}
const JOB_TYPES: {value: string; label: string; workType: PmsWorkType}[] =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter) => ({
    value: letter,
    label: letter,
    workType: JOB_TYPE_WORKTYPE_MAP[letter] ?? 'INSPECT',
  }))
const WORKTYPE_DEFAULT_JOBTYPE_MAP: Record<PmsWorkType, string> = {
  INSPECT: 'I',
  CHECK: 'G',
  OVERHAUL: 'O',
  CLEAN: 'C',
  ADJUST: 'A',
  RENEW: 'R',
  TEST: 'T',
}

// Helper to map job type letter to work type enum
const getWorkTypeFromJobType = (jobType: string | null | undefined): PmsWorkType => {
  if (!jobType) return 'INSPECT'
  const normalized = jobType.toUpperCase()
  return JOB_TYPE_WORKTYPE_MAP[normalized] ?? 'INSPECT'
}

// Helper to get job type from work type (for initial load)
const getJobTypeFromWorkType = (workType: PmsWorkType | null | undefined): string => {
  if (!workType) return 'I'
  return WORKTYPE_DEFAULT_JOBTYPE_MAP[workType] ?? 'I'
}

const SCHEDULE_TYPES: {value: PmsScheduleType; label: string}[] = [
  {value: 'TIME', label: 'Time-based'},
  {value: 'RUNNING_HOURS', label: 'Running Hours'},
  {value: 'HYBRID_WHICHEVER_FIRST', label: 'Hybrid (Whichever First)'},
  {value: 'HYBRID_BOTH_REQUIRED', label: 'Hybrid (Both Required)'},
]

const INTERVAL_UNITS: {value: PmsIntervalUnit; label: string}[] = [
  {value: 'DAYS', label: 'Days'},
  {value: 'MONTHS', label: 'Months'},
  {value: 'YEARS', label: 'Years'},
  {value: 'HOURS', label: 'Hours'},
]

const TIME_PERIODICITY_TYPES: {value: string; label: string}[] = [
  {value: 'M', label: 'Months'},
  {value: 'W', label: 'Weeks'},
  {value: 'D', label: 'Days'},
]
const HOURS_PERIODICITY_TYPES: {value: string; label: string}[] = [{value: 'H', label: 'Hours'}]
const HYBRID_PERIODICITY_TYPES: {value: string; label: string}[] = [
  ...TIME_PERIODICITY_TYPES,
  ...HOURS_PERIODICITY_TYPES,
]

const CRITICALITY_LEVELS: {value: PmsCriticality; label: string}[] = [
  {value: 'LOW', label: 'Low'},
  {value: 'MEDIUM', label: 'Medium'},
  {value: 'HIGH', label: 'High'},
  {value: 'CRITICAL', label: 'Critical'},
]

const normalizePlanLine = (p: PmsPlanLineDto): PmsPlanLineDto => {
  const scheduleType = (p.scheduleType ?? 'TIME') as PmsScheduleType
  const needsHours =
    scheduleType === 'RUNNING_HOURS' ||
    scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
    scheduleType === 'HYBRID_BOTH_REQUIRED'
  // Get job type from periodicity (plan line), or derive from workType, or default to 'I'
  const jobType = p.jobType || getJobTypeFromWorkType(p.workType) || 'I'
  return {
    ...p,
    jobType,
    workType: (p.workType ?? getWorkTypeFromJobType(jobType)) as PmsWorkType,
    scheduleType,
    criticality: (p.criticality ?? 'MEDIUM') as PmsCriticality,
    active: p.active ?? true,

    // Normalize rules based on scheduleType
    runningHourCounterId: needsHours ? p.runningHourCounterId ?? null : null,
  }
}

export const EditPlanLineModal: FC<Props> = ({visible, planLine, vesselId, onClose, onSuccess, locked = false}) => {
  const isLocked = Boolean(locked)
  const [formData, setFormData] = useState<PmsPlanLineDto>(() => normalizePlanLine(planLine))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  // Hierarchy
  const [equipment, setEquipment] = useState<EquipmentDto[]>([])
  const [subcomponents, setSubcomponents] = useState<SubcomponentDto[]>([])
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number | undefined>()
  const [runningHourCounters, setRunningHourCounters] = useState<RunningHourCounterDto[]>([])
  const [loadingCounters, setLoadingCounters] = useState(false)

  const isRunningHours =
    formData.scheduleType === 'RUNNING_HOURS' ||
    formData.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
    formData.scheduleType === 'HYBRID_BOTH_REQUIRED'
  const isTimeSchedule =
    formData.scheduleType === 'TIME' ||
    formData.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
    formData.scheduleType === 'HYBRID_BOTH_REQUIRED'

  // ---------- loaders ----------
  const loadEquipment = async () => {
    const data = await getEquipmentByVessel(vesselId)
    const list = Array.isArray(data) ? data : []
    setEquipment(list)
    return list
  }

  const loadSubcomponents = async (equipmentId: number) => {
    const data = await getSubcomponentsByEquipment(equipmentId)
    const list = Array.isArray(data) ? data : []
    setSubcomponents(list)
    return list
  }

  const loadRunningHourCounterOptions = async (equipmentId: number) => {
    setLoadingCounters(true)
    try {
      const data = await getRunningHourCounters(vesselId)
      const list = Array.isArray(data)
        ? data.filter((counter) => counter.equipmentId === equipmentId)
        : []
      setRunningHourCounters(list)
      return list
    } catch (error) {
      console.error(error)
      toast.error('Failed to load running hour counters', {position: 'top-center'})
      setRunningHourCounters([])
      return []
    } finally {
      setLoadingCounters(false)
    }
  }

  // ---------- init on open ----------
  useEffect(() => {
    if (!visible) return
    let cancelled = false

    ;(async () => {
      try {
        setErrors({})
        setFormData(normalizePlanLine(planLine))
        setSubcomponents([])
        setSelectedEquipmentId(undefined)

        const eqList = await loadEquipment()
        if (cancelled) return

        let foundEquipmentId: number | undefined = planLine.equipmentId ?? undefined
        let subsForEquipment: SubcomponentDto[] = []

        if (!foundEquipmentId && planLine.subComponentId) {
          for (const eq of eqList) {
            if (!eq?.id) continue
            const subs = await getSubcomponentsByEquipment(eq.id)
            const list = Array.isArray(subs) ? subs : []
            const hit = list.find((s) => s.id === planLine.subComponentId)
            if (hit) {
              if (cancelled) return
              foundEquipmentId = eq.id
              subsForEquipment = list
              break
            }
          }
        }

        if (foundEquipmentId) {
          setSelectedEquipmentId(foundEquipmentId)
          const subs = subsForEquipment.length
            ? subsForEquipment
            : await loadSubcomponents(foundEquipmentId)
          if (cancelled) return
          setSubcomponents(subs)
        }

        if (planLine.subComponentId) {
          setFormData((p) => ({...p, subComponentId: planLine.subComponentId ?? null}))
        }
      } catch (e) {
        console.error(e)
        toast.error('Failed to initialize edit modal', {position: 'top-center'})
      }
    })()

    return () => {
      cancelled = true
    }
  }, [visible, vesselId, planLine])

  // When equipment changes -> reload subcomponents
  useEffect(() => {
    if (!visible) return
    if (!selectedEquipmentId) {
      setSubcomponents([])
      setRunningHourCounters([])
      setFormData((p) => ({
        ...p,
        equipmentId: null,
        componentId: null,
        subComponentId: null,
        runningHourCounterId: null,
      }))
      return
    }

    setFormData((p) => ({...p, equipmentId: selectedEquipmentId, componentId: null}))
    ;(async () => {
      try {
        const subs = await loadSubcomponents(selectedEquipmentId)
        if (formData.subComponentId && !subs.some((s) => s.id === formData.subComponentId)) {
          setFormData((p) => ({...p, subComponentId: null}))
        }
        if (isRunningHours) {
          const counters = await loadRunningHourCounterOptions(selectedEquipmentId)
          setFormData((p) => {
            if (!p.runningHourCounterId) return p
            return counters.some((counter) => counter.id === p.runningHourCounterId)
              ? p
              : {...p, runningHourCounterId: null}
          })
        } else {
          setRunningHourCounters([])
          setFormData((p) => ({...p, runningHourCounterId: null}))
        }
      } catch (e) {
        console.error(e)
      }
    })()
  }, [selectedEquipmentId, visible, isRunningHours])

  // ---------- helpers ----------
  const selectedEquipmentName = useMemo(() => {
    if (!selectedEquipmentId) return null
    return equipment.find((e) => e.id === selectedEquipmentId)?.name ?? null
  }, [equipment, selectedEquipmentId])

  const selectedSubcomponentName = useMemo(() => {
    if (!formData.subComponentId) return null
    return subcomponents.find((s) => s.id === formData.subComponentId)?.name ?? null
  }, [subcomponents, formData.subComponentId])

  const getPeriodicityOptions = (scheduleType?: PmsScheduleType) => {
    if (scheduleType === 'RUNNING_HOURS') return HOURS_PERIODICITY_TYPES
    if (scheduleType === 'TIME') return TIME_PERIODICITY_TYPES
    return HYBRID_PERIODICITY_TYPES
  }

  const getPeriodicityLabel = (type?: string | null) => {
    if (!type) return ''
    const id = type.toUpperCase()
    if (id === 'H') return 'Running Hours'
    if (id === 'M') return 'Time-based (Months)'
    if (id === 'W') return 'Time-based (Weeks)'
    if (id === 'D') return 'Time-based (Days)'
    if (id === 'C') return 'Conditional'
    return id
  }

  const getPeriodicityUnitLabel = (type?: string | null) => {
    if (!type) return ''
    const id = type.toUpperCase()
    if (id === 'H') return 'Hours'
    if (id === 'M') return 'Months'
    if (id === 'W') return 'Weeks'
    if (id === 'D') return 'Days'
    if (id === 'C') return 'Conditional'
    return id
  }

  // ---------- submit ----------
  const validate = () => {
    const e: Record<string, string> = {}

    if (!formData.taskDescription?.trim()) e.taskDescription = 'Job description is required'
    if (!formData.jobType) e.jobType = 'Job type is required'
    if (!formData.scheduleType) e.scheduleType = 'Schedule type is required'
    if (!selectedEquipmentId) e.equipmentId = 'Equipment is required'
    if (!selectedEquipmentId) e.equipmentId = 'Equipment is required'
    if (isRunningHours && !formData.runningHourCounterId) {
      e.runningHourCounterId = 'Counter is required for running-hours schedules'
    }

    const isHybrid =
      formData.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
      formData.scheduleType === 'HYBRID_BOTH_REQUIRED'
    const isTimeOnly = formData.scheduleType === 'TIME'
    const isHoursOnly = formData.scheduleType === 'RUNNING_HOURS'
    const timeTypes = ['M', 'W', 'D']
    const hybridTypes = ['M', 'W', 'D', 'H']

    if (isTimeOnly || isHoursOnly || isHybrid) {
      if (!formData.jobPeriodicity || formData.jobPeriodicity <= 0)
        e.jobPeriodicity = 'Periodicity value is required'
    }

    const periodicityId = formData.periodicityId?.toUpperCase()
    if (isTimeOnly) {
      if (!periodicityId || !timeTypes.includes(periodicityId)) {
        e.periodicityId = 'Periodicity type must be M (Months), W (Weeks), or D (Days)'
      }
    }

    if (isHoursOnly) {
      if (periodicityId !== 'H') {
        e.periodicityId = 'Periodicity type must be H (Hours) for running hours schedule'
      }
    }

    if (isHybrid) {
      if (!periodicityId || !hybridTypes.includes(periodicityId)) {
        e.periodicityId = 'Periodicity type must be M (Months), W (Weeks), D (Days), or H (Hours)'
      }
    }

    if (isHybrid && formData.jobPeriodicity2 && formData.periodicityId2) {
      if (formData.jobPeriodicity2 <= 0)
        e.jobPeriodicity2 = 'Second periodicity value must be greater than 0'
      const periodicityId2 = formData.periodicityId2.toUpperCase()
      if (!hybridTypes.includes(periodicityId2)) {
        e.periodicityId2 =
          'Second periodicity type must be M (Months), W (Weeks), D (Days), or H (Hours)'
      }
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (isLocked) {
      toast.error('For this job to update, please contact your admin', {position: 'top-center'})
      return
    }
    if (!validate()) return

    setSubmitting(true)
    try {
      const dto: PmsPlanLineDto = {
        ...formData,
        jobType: formData.jobType || 'I',
        workType: getWorkTypeFromJobType(formData.jobType),
        jobInstructions: formData.jobInstructions?.trim() ? formData.jobInstructions.trim() : null,
        equipmentId: selectedEquipmentId ?? null,
        componentId: null,
        subComponentId: formData.subComponentId ?? null,
        runningHourCounterId: isRunningHours ? formData.runningHourCounterId ?? null : null,
        jobPeriodicity: formData.jobPeriodicity ?? null,
        periodicityId: formData.periodicityId ?? null,
        jobPeriodicity2: formData.jobPeriodicity2 ?? null,
        periodicityId2: formData.periodicityId2 ?? null,
        preAlertDays: formData.preAlertDays ?? null,
        preAlertHoursThreshold: formData.preAlertHoursThreshold ?? null,
        earlyTolerancePercent: formData.earlyTolerancePercent ?? null,
        earlyToleranceDays: formData.earlyToleranceDays ?? null,
        lateTolerancePercent: formData.lateTolerancePercent ?? null,
        lateToleranceDays: formData.lateToleranceDays ?? null,
        lateToleranceHours: formData.lateToleranceHours ?? null,
        active: true,
      }

      await updatePmsPlanLine(formData.id!, dto)
      toast.success('Periodicity updated successfully', {position: 'top-center'})
      onSuccess()
      onClose()
    } catch (err: any) {
      console.error(err)
      toast.error('Failed to update periodicity (plan line)', {
        position: 'top-center',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (!visible) return null

  return (
    <div
      style={WRAP_STYLE}
      tabIndex={-1}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role='dialog'
        aria-modal='true'
        style={{
          width: 'min(1400px, 96vw)',
          height: 'min(900px, 92vh)',
          background: '#ffffff',
          borderRadius: '18px',
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.22)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexShrink: 0,
          }}
        >
          <div>
            <h5
              className='mb-1'
              style={{
                margin: 0,
                color: '#111827',
                fontSize: '1.2rem',
                fontWeight: 700,
              }}
            >
              Edit Job Periodicity
            </h5>
            <div style={{color: '#6B7280', fontSize: '0.92rem'}}>
              Update the selected periodicity settings for this plan.
            </div>
            {isLocked && (
              <div className='text-danger fs-7 mt-1'>
                For this job to update, please contact your admin.
              </div>
            )}
          </div>
          <button
            type='button'
            onClick={onClose}
            className='btn btn-sm'
            style={{
              border: '1px solid #D1D5DB',
              background: '#fff',
              color: '#374151',
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              fontSize: '18px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className='d-flex flex-column flex-grow-1'
          style={{minHeight: 0}}
        >
          <div
            className='flex-grow-1 overflow-auto px-4 py-3'
            style={{maxHeight: 'calc(92vh - 140px)'}}
          >
            {isLocked && (
              <div className='alert alert-warning py-2 px-3 mb-3'>
                For this job to update, please contact your admin.
              </div>
            )}
            <fieldset disabled={isLocked || submitting} className='border-0 p-0 m-0'>
            {/* Hierarchy Information (Read-Only) */}
            <div className='mb-5'>
              <label className={LABEL}>Hierarchy (Read-Only)</label>
              <div className='border rounded p-3 bg-light-subtle'>
                <div className='d-flex flex-column gap-2'>
                  {selectedEquipmentName && (
                    <div className='d-flex align-items-center'>
                      <KTSVG
                        path='/media/icons/duotune/general/gen025.svg'
                        className='svg-icon-3 text-primary me-2'
                      />
                      <span className='text-muted me-2'>Equipment:</span>
                      <span className='fw-semibold text-primary'>{selectedEquipmentName}</span>
                    </div>
                  )}
                  {selectedSubcomponentName && (
                    <div className='d-flex align-items-center'>
                      <KTSVG
                        path='/media/icons/duotune/general/gen023.svg'
                        className='svg-icon-3 text-success me-2'
                      />
                      <span className='text-muted me-2'>Sub-Component:</span>
                      <span className='fw-semibold text-success'>{selectedSubcomponentName}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className='text-muted fs-7 mt-2'>
                <i className='bi bi-info-circle me-1'></i>
                The hierarchy cannot be changed after a periodicity (plan line) is created. To
                change the hierarchy, create a new periodicity (plan line).
              </div>
            </div>

            {/* Job Description */}
            <div className='mb-5'>
              <label className={`${LABEL} required`}>Job Description</label>
              <textarea
                className={`form-control ${errors.taskDescription ? 'is-invalid' : ''}`}
                rows={3}
                value={formData.taskDescription || ''}
                onChange={(e) => setFormData((p) => ({...p, taskDescription: e.target.value}))}
              />
              {errors.taskDescription && (
                <div className='invalid-feedback'>{errors.taskDescription}</div>
              )}
            </div>

            {/* Job Instructions */}
            <div className='mb-5'>
              <label className={LABEL}>Job Instructions</label>
              <textarea
                className='form-control'
                rows={3}
                value={formData.jobInstructions || ''}
                onChange={(e) => setFormData((p) => ({...p, jobInstructions: e.target.value}))}
                placeholder='Detailed instructions for the job...'
              />
            </div>

            {/* Job Type */}
            <div className='mb-5'>
              <label className={`${LABEL} required`}>Job Type</label>
              <SearchableSelect
                className={`form-select ${errors.jobType ? 'is-invalid' : ''}`}
                value={formData.jobType || 'I'}
                onChange={(e) => {
                  const jobType = e.target.value
                  const workType = getWorkTypeFromJobType(jobType)
                  setFormData((p) => ({...p, jobType, workType}))
                }}
              >
                {JOB_TYPES.map((jt) => (
                  <option key={jt.value} value={jt.value}>
                    {jt.label}
                  </option>
                ))}
              </SearchableSelect>
              {errors.jobType && <div className='invalid-feedback'>{errors.jobType}</div>}
            </div>

            {/* Schedule Type */}
            <div className='mb-5'>
              <label className={`${LABEL} required`}>Schedule Type</label>
              <SearchableSelect
                className={`form-select ${errors.scheduleType ? 'is-invalid' : ''}`}
                value={formData.scheduleType as any}
                onChange={(e) => {
                  const st = e.target.value as PmsScheduleType
                  const isHybrid = st === 'HYBRID_WHICHEVER_FIRST' || st === 'HYBRID_BOTH_REQUIRED'
                  const needsTime = st === 'TIME' || isHybrid
                  const needsHours = st === 'RUNNING_HOURS' || isHybrid
                  setFormData((p) => ({
                    ...p,
                    scheduleType: st,
                    runningHourCounterId: needsHours ? p.runningHourCounterId ?? null : null,
                  }))
                  if (!needsHours) setErrors((prev) => ({...prev, runningHourCounterId: ''}))
                }}
              >
                {SCHEDULE_TYPES.map((st) => (
                  <option key={st.value} value={st.value}>
                    {st.label}
                  </option>
                ))}
              </SearchableSelect>
              {errors.scheduleType && <div className='invalid-feedback'>{errors.scheduleType}</div>}
            </div>

            {/* Periodicities */}
            {(isTimeSchedule ||
              isRunningHours ||
              formData.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
              formData.scheduleType === 'HYBRID_BOTH_REQUIRED') && (
              <div className='mb-5'>
                <label className={LABEL}>Periodicities</label>
                <div className='border rounded p-3 bg-light'>
                  <div className='row mb-3'>
                    <div className='col-md-6'>
                      <label className='form-label fs-7 text-muted mb-1'>
                        Periodicity 1
                        {formData.periodicityId && (
                          <span
                            className={`badge badge-sm ms-2 ${
                              formData.periodicityId?.toUpperCase() === 'H'
                                ? 'badge-light-success'
                                : 'badge-light-primary'
                            }`}
                          >
                            {getPeriodicityLabel(formData.periodicityId)}
                          </span>
                        )}
                      </label>
                      <div className='row g-2'>
                        <div className='col-8'>
                          <input
                            type='number'
                            className='form-control form-control-sm'
                            placeholder='Value'
                            min={1}
                            value={formData.jobPeriodicity ?? ''}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                jobPeriodicity: e.target.value ? Number(e.target.value) : null,
                              }))
                            }
                          />
                        </div>
                        <div className='col-4'>
                          <SearchableSelect
                            className='form-select form-select-sm'
                            value={formData.periodicityId || ''}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                periodicityId: e.target.value || null,
                              }))
                            }
                          >
                            <option value=''>Type</option>
                            {getPeriodicityOptions(formData.scheduleType).map((pt) => (
                              <option key={pt.value} value={pt.value}>
                                {pt.label}
                              </option>
                            ))}
                          </SearchableSelect>
                        </div>
                      </div>
                      {formData.jobPeriodicity && formData.periodicityId && (
                        <div className='text-muted fs-8 mt-1'>
                          Current: {formData.jobPeriodicity}{' '}
                          {getPeriodicityUnitLabel(formData.periodicityId)}
                        </div>
                      )}
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label fs-7 text-muted mb-1'>
                        Periodicity 2
                        {formData.periodicityId2 && (
                          <span
                            className={`badge badge-sm ms-2 ${
                              formData.periodicityId2?.toUpperCase() === 'H'
                                ? 'badge-light-success'
                                : 'badge-light-primary'
                            }`}
                          >
                            {getPeriodicityLabel(formData.periodicityId2)}
                          </span>
                        )}
                      </label>
                      <div className='row g-2'>
                        <div className='col-8'>
                          <input
                            type='number'
                            className='form-control form-control-sm'
                            placeholder='Value'
                            min={1}
                            value={formData.jobPeriodicity2 ?? ''}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                jobPeriodicity2: e.target.value ? Number(e.target.value) : null,
                              }))
                            }
                          />
                        </div>
                        <div className='col-4'>
                          <SearchableSelect
                            className='form-select form-select-sm'
                            value={formData.periodicityId2 || ''}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                periodicityId2: e.target.value || null,
                              }))
                            }
                          >
                            <option value=''>Type</option>
                            {getPeriodicityOptions(formData.scheduleType).map((pt) => (
                              <option key={pt.value} value={pt.value}>
                                {pt.label}
                              </option>
                            ))}
                          </SearchableSelect>
                        </div>
                      </div>
                      {formData.jobPeriodicity2 && formData.periodicityId2 && (
                        <div className='text-muted fs-8 mt-1'>
                          Current: {formData.jobPeriodicity2}{' '}
                          {getPeriodicityUnitLabel(formData.periodicityId2)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='text-muted fs-7'>
                    <i className='bi bi-info-circle me-1'></i>
                    For hybrid schedules, specify both periodicities. The system will use whichever
                    comes first (or both if required).
                    <br />
                    <strong>H = Running Hours</strong> (based on equipment running hour counter) |{' '}
                    <strong>M/W/D = Time-based</strong> (based on calendar date)
                  </div>
                </div>
              </div>
            )}

            {isRunningHours && selectedEquipmentId && (
              <div className='mb-5'>
                <label className={LABEL}>
                  Running Hour Counter <span className='text-danger'>*</span>
                </label>
                {loadingCounters ? (
                  <div className='text-muted'>Loading counters...</div>
                ) : runningHourCounters.length > 0 ? (
                  <>
                    <SearchableSelect
                      className={`form-select ${errors.runningHourCounterId ? 'is-invalid' : ''}`}
                      value={formData.runningHourCounterId ?? ''}
                      onChange={(e) => {
                        setFormData((p) => ({
                          ...p,
                          runningHourCounterId: e.target.value ? Number(e.target.value) : null,
                        }))
                        if (errors.runningHourCounterId) {
                          setErrors((prev) => ({...prev, runningHourCounterId: ''}))
                        }
                      }}
                    >
                      <option value=''>Select Counter</option>
                      {runningHourCounters.map((counter) => (
                        <option key={counter.id} value={counter.id}>
                          {counter.name} (Current: {counter.currentValue})
                        </option>
                      ))}
                    </SearchableSelect>
                    {errors.runningHourCounterId && (
                      <div className='invalid-feedback d-block'>{errors.runningHourCounterId}</div>
                    )}
                    <div className='text-muted fs-7 mt-2'>
                      This counter will be used for due calculations and job generation.
                    </div>
                  </>
                ) : (
                  <div className='border rounded p-2 bg-light-warning'>
                    <span className='text-warning'>
                      No running hour counters found for this equipment.
                    </span>
                    <div className='text-muted fs-7 mt-1'>
                      Create a counter in Running Hours first.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Criticality */}
            <div className='mb-5'>
              <label className={LABEL}>Criticality</label>
              <SearchableSelect
                className='form-select'
                value={formData.criticality as any}
                onChange={(e) =>
                  setFormData((p) => ({...p, criticality: e.target.value as PmsCriticality}))
                }
              >
                {CRITICALITY_LEVELS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </SearchableSelect>
            </div>
            </fieldset>
          </div>

          <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0'>
            <button
              type='button'
              className='btn btn-light btn-sm'
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type='submit' className='btn btn-primary btn-sm' disabled={submitting || isLocked}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
